import type { AstNode, CommentProvider, GrammarAST, MaybePromise, ReferenceInfo } from "langium";
import {
	type CompletionAcceptor,
	type CompletionContext,
	type CompletionProviderOptions,
	DefaultCompletionProvider,
	type LangiumServices,
	type NextFeature,
} from "langium/lsp";
import { CompletionItemKind, type MarkupContent } from "vscode-languageserver";
import {
	type ReferenceDirective,
	isDeclaration,
	isDirective,
	isDirectiveParameter,
	isImport,
	isImportMember,
	isImportable,
	isModule,
	isReferenceDirective,
	isReferenceDirectiveArgument,
	isReferenceType,
	isReferenceTypeOption,
	isScalarOption,
	isType,
} from "../generated/ast.js";

export class IprotoCompletionProvider extends DefaultCompletionProvider {
	private commentProvider: CommentProvider;

	override readonly completionOptions?: CompletionProviderOptions;

	constructor(services: LangiumServices) {
		super(services);

		this.commentProvider = services.documentation.CommentProvider;

		this.completionOptions = {
			triggerCharacters: [":", "#", "<"],
		};
	}

	private completionForReferenceTypeOption(
		context: CompletionContext,
		next: NextFeature<GrammarAST.CrossReference>,
		acceptor: CompletionAcceptor,
	) {
		const node = context.node;

		if (isReferenceTypeOption(node)) {
			const property: keyof ReferenceDirective = "target";

			const refInfo: ReferenceInfo = {
				reference: {
					$refText: "",
				},
				container: node,
				property,
			};

			for (const element of this.scopeProvider.getScope(refInfo).getAllElements()) {
				if (isScalarOption(element.node)) {
					let insertText = element.node.name;

					if (element.node.type === "int" || element.node.type === "float") {
						insertText += ":0";
					}
					if (element.node.type === "string") {
						insertText += `:""`;
					}
					if (element.node.type === "boolean") {
						insertText += ":true";
					}

					acceptor(context, {
						label: element.name,
						labelDetails: {
							description: `${element.node.type}`,
						},
						kind: CompletionItemKind.Field,
						documentation: this.formatComment(element.node),
						insertText,
					});
				}
			}

			return;
		}
	}

	private completionForReferenceDirective(
		context: CompletionContext,
		next: NextFeature<GrammarAST.CrossReference>,
		acceptor: CompletionAcceptor,
	) {
		const node = context.node;

		if (isReferenceDirective(node)) {
			const property: keyof ReferenceDirective = "target";

			const refInfo: ReferenceInfo = {
				reference: {
					$refText: "",
				},
				container: node,
				property,
			};

			for (const element of this.scopeProvider.getScope(refInfo).getAllElements()) {
				if (isDirective(element.node)) {
					acceptor(context, {
						label: element.name,
						labelDetails: {
							description: `on ${element.node.locations.join(" | ").trim()}`,
						},
						kind: CompletionItemKind.Field,
						documentation: this.formatComment(element.node),
					});
				}
			}

			return;
		}
	}

	private completionForReferenceDirectiveArgument(
		context: CompletionContext,
		next: NextFeature<GrammarAST.CrossReference>,
		acceptor: CompletionAcceptor,
	) {
		const node = context.node;

		if (isReferenceDirectiveArgument(node)) {
			const property: keyof ReferenceDirective = "target";

			const refInfo: ReferenceInfo = {
				reference: {
					$refText: "",
				},
				container: node,
				property,
			};

			for (const element of this.scopeProvider.getScope(refInfo).getAllElements()) {
				if (isDirectiveParameter(element.node)) {
					let insertText = element.node.name;

					if (element.node.type === "int" || element.node.type === "float") {
						insertText += ":0";
					}
					if (element.node.type === "string") {
						insertText += `:""`;
					}
					if (element.node.type === "boolean") {
						insertText += ":true";
					}

					acceptor(context, {
						label: element.name,
						labelDetails: {
							description: `${element.node.type}${element.node.optional ? "?" : ""}`,
						},
						kind: CompletionItemKind.Field,
						documentation: this.formatComment(element.node),
						insertText,
					});
				}
			}

			return;
		}
	}

	private completionForReferenceType(
		context: CompletionContext,
		next: NextFeature<GrammarAST.CrossReference>,
		acceptor: CompletionAcceptor,
	) {
		const node = context.node;

		if (isReferenceType(node)) {
			const property: keyof ReferenceDirective = "target";

			const refInfo: ReferenceInfo = {
				reference: {
					$refText: "",
				},
				container: node,
				property,
			};

			for (const element of this.scopeProvider.getScope(refInfo).getAllElements()) {
				if (isType(element.node)) {
					acceptor(context, {
						label: element.name,
						labelDetails: {
							description: element.node.$type,
						},
						kind: CompletionItemKind.Field,
						documentation: this.formatComment(element.node),
					});
				}
			}

			return;
		}
	}

	private completionForImport(
		context: CompletionContext,
		next: NextFeature<GrammarAST.CrossReference>,
		acceptor: CompletionAcceptor,
	) {
		const node = context.node;

		if (isImport(node)) {
			const property: keyof ReferenceDirective = "target";

			const refInfo: ReferenceInfo = {
				reference: {
					$refText: "",
				},
				container: node,
				property,
			};

			for (const element of this.scopeProvider.getScope(refInfo).getAllElements()) {
				if (isModule(element.node)) {
					acceptor(context, {
						label: element.name,
						labelDetails: {
							description: element.node.$type,
						},
						kind: CompletionItemKind.Field,
						documentation: this.formatComment(element.node),
					});
				}
			}

			return;
		}
	}

	private completionForImportMember(
		context: CompletionContext,
		next: NextFeature<GrammarAST.CrossReference>,
		acceptor: CompletionAcceptor,
	) {
		const node = context.node;

		if (isImportMember(node)) {
			const property: keyof ReferenceDirective = "target";

			const refInfo: ReferenceInfo = {
				reference: {
					$refText: "",
				},
				container: node,
				property,
			};

			for (const element of this.scopeProvider.getScope(refInfo).getAllElements()) {
				if (isImportable(element.node)) {
					acceptor(context, {
						label: element.name,
						labelDetails: {
							description: element.node.$type,
						},
						kind: CompletionItemKind.Field,
						documentation: this.formatComment(element.node),
					});
				}
			}

			return;
		}
	}

	private formatComment(node: AstNode): MarkupContent | undefined {
		const contents: MarkupContent = {
			kind: "markdown",
			value: "",
		};

		this.documentationProvider.getDocumentation(node);

		const comment = this.commentProvider.getComment(node);

		if (comment) {
			return {
				kind: "markdown",
				value: comment.substring(2, comment.length - 2).trim(),
			};
		}

		return contents;
	}

	protected override completionForCrossReference(
		context: CompletionContext,
		next: NextFeature<GrammarAST.CrossReference>,
		acceptor: CompletionAcceptor,
	) {
		const node = context.node;

		if (!node) {
			return super.completionForCrossReference(context, next, acceptor);
		}

		if (isReferenceTypeOption(node)) {
			this.completionForReferenceTypeOption(context, next, acceptor);
		} else if (isReferenceDirective(node)) {
			this.completionForReferenceDirective(context, next, acceptor);
		} else if (isReferenceDirectiveArgument(node)) {
			this.completionForReferenceDirectiveArgument(context, next, acceptor);
		} else if (isReferenceType(node)) {
			this.completionForReferenceType(context, next, acceptor);
		} else if (isImport(node)) {
			this.completionForImport(context, next, acceptor);
		} else if (isImportMember(node)) {
			this.completionForImportMember(context, next, acceptor);
		}
	}

	protected override completionForKeyword(
		context: CompletionContext,
		keyword: GrammarAST.Keyword,
		acceptor: CompletionAcceptor,
	): MaybePromise<void> {
		if (isDeclaration(context.node)) {
			for (const keyword of ["use", "directive", "scalar", "enum", "struct", "service", "exception", "alias"]) {
				acceptor(context, {
					label: keyword,
					kind: CompletionItemKind.Keyword,
				});
			}
			return;
		}

		return super.completionForKeyword(context, keyword, acceptor);
	}

	protected override completionFor(
		context: CompletionContext,
		next: NextFeature,
		acceptor: CompletionAcceptor,
	): MaybePromise<void> {
		return super.completionFor(context, next, acceptor);
	}
}
